import React from 'react';
import type { PlanningDraftState } from '../../../features/disposition-planning';
import {
  LANE_HEIGHT,
  MIN_WORK_MINUTES,
  ROW_PADDING_Y,
  toXPx,
  type PreviewLayout,
  type SegmentHandle,
} from '../../../shared/lib/schedule';
import { HandleStrip } from './HandleStrip';

/** Below this many pixels a strip drops its `45 min` label — same rule as `DragGhost`. */
const SEGMENT_LABEL_MIN_WIDTH = 34;

/** Blue, not the amber of `needsRescheduling`: this is work being planned, not work displaced. */
const DRAFT_OUTLINE = '2px dashed #2563EB';

interface Props {
  draft: PlanningDraftState;
  /** Prepared by the widget from `draftLayout`, colours already resolved per mode. */
  layout: PreviewLayout;
  /** Advisory red — the confirm still goes through, the server is the authority. */
  colliding: boolean;
  /** Advisory amber — the draft sits outside the technician's shift. Also does not block the confirm. */
  outsideShift: boolean;
  /** The draft is mid-drag — dim it, the ghost shows where it will land. */
  isDragging: boolean;
  onDragStart: (handle: 'move' | SegmentHandle, e: React.PointerEvent) => void;
}

const HANDLE_LABEL: Record<SegmentHandle, string> = {
  travel: 'Anfahrt anpassen',
  work: 'Arbeitszeit anpassen',
  departure: 'Abfahrt anpassen',
};

/**
 * The placed-but-not-saved draft, drawn as a first-class block: same lane geometry as
 * `ScheduleBlock`, same handle strips, same drag primitive behind them. Dashed outline and a washed
 * fill keep it readable as "not booked yet".
 *
 * Always lane 0 — the draft is being placed, not packed against anything.
 */
export const PlanningDraftBlock = ({
  draft,
  layout,
  colliding,
  outsideShift,
  isDragging,
  onDragStart,
}: Props) => {
  const widthPx = Math.max(
    toXPx(layout.endMin - layout.startMin),
    toXPx(MIN_WORK_MINUTES),
  );
  const total = layout.segments.reduce((sum, segment) => sum + segment.minutes, 0);
  const px = (minutes: number) => (total > 0 ? (minutes / total) * widthPx : 0);

  /** Each handle sits on the boundary it moves — mirrors `ScheduleBlock.handleOffset`. */
  const handleOffset: Record<SegmentHandle, number> = {
    travel: 0,
    work: px(draft.durations.travel + draft.durations.work),
    departure: widthPx,
  };

  return (
    <div
      role="button"
      tabIndex={-1}
      aria-label="Geplanter Eintrag – ziehen zum Verschieben"
      // The board's scroll-into-view looks the draft up by this attribute rather than re-deriving
      // its x from `toXPx` and its y from the lane stack. There is only ever one.
      data-planning-draft

      // The trailing click of a body drag bubbles to the row track, which would re-place the draft;
      // `BoardRow` guards that with `wasDragged()`. A plain click on the draft itself is just eaten.
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => onDragStart('move', e)}
      className={[
        'absolute isolate flex gap-px overflow-hidden rounded cursor-grab active:cursor-grabbing',
        isDragging ? 'opacity-30' : 'opacity-90',
      ].join(' ')}
      style={{
        left: toXPx(layout.startMin),
        width: widthPx,
        top: ROW_PADDING_Y,
        height: LANE_HEIGHT - 6,
        outline: DRAFT_OUTLINE,
        outlineOffset: '-2px',
      }}
    >
      {layout.segments.map((segment) => {
        if (segment.minutes <= 0) return null;
        const segmentWidth = px(segment.minutes);

        return (
          <div
            key={segment.kind}
            className="flex h-full min-w-0 items-center justify-center overflow-hidden rounded-sm"
            style={{ flex: segment.minutes, backgroundColor: segment.color, minWidth: 4 }}
          >
            {segmentWidth >= SEGMENT_LABEL_MIN_WIDTH && (
              <span className="truncate px-1 text-[10px] font-semibold tabular-nums text-white">
                {segment.label}
              </span>
            )}
          </div>
        );
      })}

      {/* Same advisory tints as the drag ghost, same precedence — the strips stay readable underneath. */}
      {colliding && <div className="pointer-events-none absolute inset-0 bg-red-600/45" />}
      {!colliding && outsideShift && (
        <div className="pointer-events-none absolute inset-0 bg-amber-500/40" />
      )}

      {!isDragging &&
        (['travel', 'work', 'departure'] as const).map((handle) => (
          <HandleStrip
            key={handle}
            leftPx={handleOffset[handle]}
            label={HANDLE_LABEL[handle]}
            onPointerDown={(e) => onDragStart(handle, e)}
            onHoverChange={() => {}}
          />
        ))}
    </div>
  );
};
